import React, { useState, useEffect, useRef } from 'react';

/**
 * ResizablePanel - A component that allows panels to be resized by the user
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display in the panels
 * @param {string} props.direction - Direction of the split: 'horizontal' or 'vertical'
 * @param {number} props.initialSize - Initial size in percentage (0-100) of the first panel
 * @param {number} props.minSize - Minimum size in percentage for any panel
 * @param {function} props.onResize - Callback when resizing occurs
 * @param {string} props.className - Additional CSS class names
 */
const ResizablePanel = ({ 
  children, 
  direction = 'horizontal', 
  initialSize = 50, 
  minSize = 10, 
  onResize,
  className = ''
}) => {
  const [size, setSize] = useState(initialSize);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  const handleRef = useRef(null);
  
  // Get the children array safely
  const childrenArray = React.Children.toArray(children);
  const hasTwoChildren = childrenArray.length === 2;

  // Handle mouse down on the resize handle
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle mouse move during resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      let newSize;

      if (direction === 'horizontal') {
        const offsetX = e.clientX - containerRect.left;
        newSize = (offsetX / containerRect.width) * 100;
      } else {
        const offsetY = e.clientY - containerRect.top;
        newSize = (offsetY / containerRect.height) * 100;
      }

      // Clamp size between minSize and (100 - minSize)
      newSize = Math.max(minSize, Math.min(100 - minSize, newSize));
      
      setSize(newSize);
      
      if (onResize) {
        onResize(newSize);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, direction, minSize, onResize]);

  // Set cursor style on body during resize
  useEffect(() => {
    if (isResizing) {
      document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, direction]);

  // If we don't have exactly two children, render an error message
  if (!hasTwoChildren) {
    console.error('ResizablePanel requires exactly two children');
    return <div className="p-4 text-red-500">ResizablePanel requires exactly two children</div>;
  }

  return (
    <div 
      ref={containerRef}
      className={`flex ${direction === 'horizontal' ? 'flex-row' : 'flex-col'} w-full h-full ${className}`}
    >
      {/* First Panel */}
      <div 
        className="overflow-auto"
        style={{ 
          [direction === 'horizontal' ? 'width' : 'height']: `${size}%`,
          transition: isResizing ? 'none' : 'all 0.1s ease',
          minWidth: direction === 'horizontal' ? `${minSize}%` : undefined,
          minHeight: direction === 'vertical' ? `${minSize}%` : undefined,
        }}
      >
        {childrenArray[0]}
      </div>

      {/* Resize Handle */}
      <div
        ref={handleRef}
        className={`
          flex items-center justify-center
          ${direction === 'horizontal' 
            ? 'cursor-col-resize w-1.5 bg-gray-200 hover:bg-indigo-400 active:bg-indigo-500' 
            : 'cursor-row-resize h-1.5 bg-gray-200 hover:bg-indigo-400 active:bg-indigo-500'}
          transition-colors duration-150
          ${isResizing ? 'bg-indigo-500' : ''}
        `}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        <div className={`
          ${direction === 'horizontal'
            ? 'w-0.5 h-8 bg-gray-400'
            : 'h-0.5 w-8 bg-gray-400'}
          ${isResizing ? 'bg-white' : ''}
        `}></div>
      </div>

      {/* Second Panel */}
      <div 
        className="overflow-auto"
        style={{ 
          [direction === 'horizontal' ? 'width' : 'height']: `${100 - size}%`,
          transition: isResizing ? 'none' : 'all 0.1s ease',
          minWidth: direction === 'horizontal' ? `${minSize}%` : undefined,
          minHeight: direction === 'vertical' ? `${minSize}%` : undefined,
        }}
      >
        {childrenArray[1]}
      </div>
    </div>
  );
};

export default ResizablePanel;